import React, { useState, useEffect } from 'react';
import { FiCamera, FiTrash2, FiStar, FiUpload, FiAlertCircle } from 'react-icons/fi';
import { productosService } from '../../services';
import './ProductImageGallery.scss';

/**
 * Componente para gestión de múltiples imágenes de producto
 * Soporta hasta 10 imágenes con metadatos, reordenamiento y marcado de imagen principal
 */
const ProductImageGallery = ({ productId, onImagesChange }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const MAX_IMAGES = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  // Cargar imágenes existentes cuando hay un productId
  useEffect(() => {
    if (productId) {
      loadImages();
    }
  }, [productId]);

  const loadImages = async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await productosService.getProductImages(productId);

      if (response.success) {
        setImages(response.data || []);
        if (onImagesChange) {
          onImagesChange(response.data || []);
        }
      } else {
        setError(response.error || 'Error al cargar las imágenes');
      }
    } catch (err) {
      console.error('Error cargando imágenes:', err);
      setError('Error al cargar las imágenes del producto');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    // Validar que no exceda el máximo de imágenes
    if (images.length + files.length > MAX_IMAGES) {
      setError(`Máximo ${MAX_IMAGES} imágenes permitidas. Actualmente tienes ${images.length}.`);
      return;
    }

    // Validar cada archivo
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`Tipo de archivo no permitido: ${file.type}. Use JPG, PNG, WEBP o GIF.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`El archivo ${file.name} excede el tamaño máximo de 10MB.`);
        return;
      }
    }

    // Subir archivos uno por uno
    setUploading(true);
    setError(null);

    for (const file of files) {
      try {
        const isPrimary = images.length === 0; // Primera imagen es principal
        const displayOrder = images.length;

        const response = await productosService.uploadProductImage(productId, file, {
          alt_text: `Imagen de producto`,
          is_primary: isPrimary,
          display_order: displayOrder
        });

        if (response.success) {
          setImages(prev => [...prev, response.data]);
        } else {
          setError(response.error || `Error al subir ${file.name}`);
          break;
        }
      } catch (err) {
        console.error('Error subiendo imagen:', err);
        setError(`Error al subir ${file.name}`);
        break;
      }
    }

    setUploading(false);
    event.target.value = ''; // Reset input

    // Recargar imágenes para obtener el orden actualizado
    await loadImages();
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('¿Está seguro de eliminar esta imagen?')) {
      return;
    }

    setError(null);

    try {
      const response = await productosService.deleteProductImage(productId, imageId);

      if (response.success) {
        setImages(prev => prev.filter(img => img.id !== imageId));
        await loadImages(); // Recargar para actualizar orden
      } else {
        setError(response.error || 'Error al eliminar la imagen');
      }
    } catch (err) {
      console.error('Error eliminando imagen:', err);
      setError('Error al eliminar la imagen');
    }
  };

  const handleSetPrimary = async (imageId) => {
    setError(null);

    try {
      // Actualizar la imagen seleccionada como principal
      const response = await productosService.updateImageMetadata(productId, imageId, {
        is_primary: true
      });

      if (response.success) {
        await loadImages(); // Recargar para reflejar cambios
      } else {
        setError(response.error || 'Error al establecer imagen principal');
      }
    } catch (err) {
      console.error('Error estableciendo imagen principal:', err);
      setError('Error al establecer la imagen principal');
    }
  };

  const getImageUrl = (image) => {
    // Las imágenes se sirven desde la raíz del servidor, sin /api/v1
    // Extraer solo el host base de REACT_APP_API_URL (remover /api/v1)
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, ''); // http://localhost:8000

    // Usar la URL medium para el preview
    if (image.medium_url) {
      return `${baseUrl}${image.medium_url}`;
    }
    if (image.url) {
      return `${baseUrl}${image.url}`;
    }
    return '';
  };

  // Si no hay productId, mostrar mensaje informativo
  if (!productId) {
    return (
      <div className="product-image-gallery">
        <div className="gallery-placeholder">
          <FiAlertCircle className="placeholder-icon" />
          <p className="placeholder-text">
            Guarde el producto primero para poder agregar imágenes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-image-gallery">
      {/* Header con botón de subida */}
      <div className="gallery-header">
        <div className="gallery-info">
          <h3 className="gallery-title">Imágenes del Producto</h3>
          <span className="image-count">
            {images.length} / {MAX_IMAGES} imágenes
          </span>
        </div>

        {images.length < MAX_IMAGES && (
          <label className="upload-button">
            <FiUpload className="upload-icon" />
            <span>Subir Imágenes</span>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileSelect}
              disabled={uploading || loading}
              style={{ display: 'none' }}
            />
          </label>
        )}
      </div>

      {/* Mensajes de error */}
      {error && (
        <div className="gallery-error">
          <FiAlertCircle className="error-icon" />
          <span>{error}</span>
        </div>
      )}

      {/* Estado de carga */}
      {(loading || uploading) && (
        <div className="gallery-loading">
          <div className="loading-spinner"></div>
          <span>{uploading ? 'Subiendo imágenes...' : 'Cargando...'}</span>
        </div>
      )}

      {/* Grid de imágenes */}
      {!loading && images.length > 0 && (
        <div className="images-grid">
          {images.map((image) => (
            <div key={image.id} className="image-card">
              {/* Badge de imagen principal */}
              {image.is_primary && (
                <div className="primary-badge">
                  <FiStar className="star-icon" />
                  <span>Principal</span>
                </div>
              )}

              {/* Imagen preview */}
              <div className="image-preview">
                <img
                  src={getImageUrl(image)}
                  alt={image.alt_text || 'Producto'}
                />
              </div>

              {/* Acciones */}
              <div className="image-actions">
                {!image.is_primary && (
                  <button
                    type="button"
                    className="action-button set-primary"
                    onClick={() => handleSetPrimary(image.id)}
                    title="Establecer como principal"
                  >
                    <FiStar />
                  </button>
                )}

                <button
                  type="button"
                  className="action-button delete"
                  onClick={() => handleDeleteImage(image.id)}
                  title="Eliminar imagen"
                >
                  <FiTrash2 />
                </button>
              </div>

              {/* Info de la imagen */}
              <div className="image-info">
                <span className="image-size">
                  {image.file_size ? `${(image.file_size / 1024).toFixed(1)} KB` : ''}
                </span>
                {image.width && image.height && (
                  <span className="image-dimensions">
                    {image.width} × {image.height}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Estado vacío */}
      {!loading && images.length === 0 && (
        <div className="gallery-empty">
          <FiCamera className="empty-icon" />
          <p className="empty-text">No hay imágenes todavía</p>
          <p className="empty-hint">
            Haga clic en "Subir Imágenes" para agregar hasta {MAX_IMAGES} imágenes
          </p>
        </div>
      )}

      {/* Ayuda */}
      <div className="gallery-help">
        <small>
          Formatos permitidos: JPG, PNG, WEBP, GIF. Tamaño máximo: 10MB por imagen.
          La primera imagen será la principal por defecto.
        </small>
      </div>
    </div>
  );
};

export default ProductImageGallery;
